import { Dispatch } from '@reduxjs/toolkit';
import { CmcWebApi, IWebApiRequestBody } from '@cmctechnology/webinvest-api-client-web-trading';
import { ORDERS_STORE_NAME, actions, IStore } from './store';
import { AccountKey } from './types';
import { frontendActions, getConfig, LocationType, ResourceKey } from '@cmctechnology/webinvest-store-frontend';
import { ORDER_API_NAMES } from './apis';

export type OrderHistoryArgs = { accountNo: AccountKey; from: number; to: number; pageSize: number; page: number };

export const makeAccountOrderHistoryApiKey = (accountNo: AccountKey, from: Date, to: Date, pageSize: number, page: number) =>
  `${accountNo}_${from.valueOf()}_${to.valueOf()}_${pageSize}_${page}`;
export const makeAccountOrderHistorySubscriptionKey = (accountNo: AccountKey, from: Date, to: Date, pageSize: number, page: number) =>
  JSON.stringify({ accountNo, from: from.valueOf(), to: to.valueOf(), pageSize, page } as OrderHistoryArgs);

export const fetchOrderHistory = (accountNo: AccountKey, from: Date, to: Date, pageSize: number, page: number) => {
  return async (dispatch: Dispatch<any>, getState: () => IStore) => {
    const hash = getState()[ORDERS_STORE_NAME].history[accountNo]?.from[from.valueOf()]?.to[to.valueOf()]?.pageSize[pageSize]?.page[page]?.hash ?? 0;
    const request: Partial<CmcWebApi.Account.OrderHistoryRequest2> & IWebApiRequestBody = {
      AccountNo: accountNo,
      StartDate: from.toISOString(),
      EndDate: to.toISOString(),
      PageSize: pageSize,
      ResultsPage: page,
      _config: {
        hash
      }
    };

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApi.Account.OrderHistoryModel3>(
        ORDERS_STORE_NAME,
        ORDER_API_NAMES.OrderHistoryGet3,
        request,
        dispatch,
        makeAccountOrderHistoryApiKey(accountNo, from, to, pageSize, page),
        false,
        true
      );
      dispatch(
        actions.orderHistoryFetched({
          accountNo,
          from: from.valueOf(),
          to: to.valueOf(),
          items: response.Orders,
          hash: response._apiInfo?.Hash ?? 0,
          page,
          pageCount: response.Paging.TotalPages,
          resultCount: response.Paging.TotalResults,
          pageSize
        })
      );
    } catch {}
  };
};

export const onPollOrderHistory =
  (subscriptions: ResourceKey[] = []) =>
  (dispatch: Dispatch<any>) => {
    subscriptions.forEach((sub) => {
      const args = JSON.parse(sub.toString()) as OrderHistoryArgs;
      dispatch<any>(fetchOrderHistory(args.accountNo, new Date(args.from), new Date(args.to), args.pageSize, args.page));
    });
  };

export const subscribeOrderHistory =
  (location: LocationType, accountNo: AccountKey, from: Date, to: Date, pageSize: number, page: number) => (dispatch: Dispatch<any>) => {
    const key = makeAccountOrderHistorySubscriptionKey(accountNo, from, to, pageSize, page);
    dispatch<any>(
      frontendActions.subscribe({
        onPoll: onPollOrderHistory,
        location,
        apiName: ORDER_API_NAMES.OrderHistoryGet3,
        storeName: ORDERS_STORE_NAME,
        resourceKeys: [key],
        pollTimeout: getConfig().pollTimeout
      })
    );
  };

export const unsubscribeOrderHistory =
  (location: LocationType, accountNo: AccountKey, from: Date, to: Date, pageSize: number, page: number) => (dispatch: Dispatch<any>) => {
    const key = makeAccountOrderHistorySubscriptionKey(accountNo, from, to, pageSize, page);
    dispatch<any>(
      frontendActions.unsubscribe({
        location,
        apiName: ORDER_API_NAMES.OrderHistoryGet3,
        storeName: ORDERS_STORE_NAME,
        resourceKeys: [key]
      })
    );
  };
